import React from "react";
import { Link } from "react-router-dom";
import { Phone, Clock, Mail, ExternalLink, ShieldCheck } from "lucide-react";
import { YELP_URL, EMAIL } from "../../lib/content";
import { LicenseLink } from "../LicenseLink";

const HEADING = "font-display font-bold text-xs uppercase tracking-widest text-brand-accent";

export const FooterBrand = ({ t }) => (
  <div className="md:col-span-5">
    <div className="flex items-center gap-3">
      <img
        src="/logo-sm.png"
        alt="Tenorio Master Roofing logo"
        data-testid="footer-logo-img"
        className="h-16 w-auto object-contain rounded-md ring-1 ring-white/20"
      />
      <div className="leading-tight">
        <div className="font-display font-extrabold text-white text-lg">Tenorio Master</div>
        <div className="text-[11px] text-slate-400 font-medium tracking-wider uppercase">Roofing</div>
      </div>
    </div>
    <p className="mt-5 text-slate-400 text-sm md:text-base max-w-md leading-relaxed">{t.footer.tagline}</p>
  </div>
);

export const FooterContact = ({ t }) => (
  <div className="md:col-span-3">
    <h4 className={`${HEADING} mb-4`}>{t.contact.phoneLabel}</h4>
    <a
      href={`tel:${t.phone.tel}`}
      data-testid="footer-phone"
      className="font-display font-black text-xl md:text-2xl hover:text-brand-accent transition-colors flex items-center gap-2"
    >
      <Phone className="w-4 h-4" strokeWidth={2.5} />
      {t.phone.display}
    </a>
    <h4 className={`${HEADING} mt-6 mb-3`}>{t.contact.emailLabel}</h4>
    <a
      href={`mailto:${EMAIL}`}
      data-testid="footer-email"
      className="font-display font-bold text-sm text-slate-200 hover:text-brand-accent transition-colors flex items-center gap-2 break-all"
    >
      <Mail className="w-4 h-4 flex-shrink-0" strokeWidth={2.5} />
      {EMAIL}
    </a>
  </div>
);

export const FooterHours = ({ t }) => (
  <div className="md:col-span-4">
    <h4 className={`${HEADING} mb-4 flex items-center gap-2`}>
      <Clock className="w-3.5 h-3.5" strokeWidth={2.5} />
      {t.contact.hoursTitle}
    </h4>
    <div className="space-y-1.5 text-sm text-slate-300">
      <div className="flex justify-between gap-4">
        <span>{t.contact.monSat}</span>
        <span className="text-white font-medium">{t.contact.monSatHours}</span>
      </div>
      <div className="flex justify-between gap-4">
        <span>{t.contact.sun}</span>
        <span className="text-white font-medium">{t.contact.sunHours}</span>
      </div>
    </div>
  </div>
);

export const FooterBottom = ({ t }) => (
  <>
    <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-slate-500">
      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
        <span data-testid="footer-dba">{t.footer.dba}</span>
        <span className="hidden sm:inline">·</span>
        <LicenseLink
          t={t}
          testId="footer-license"
          className="text-slate-300 font-semibold hover:text-brand-accent transition-colors underline decoration-dotted underline-offset-4"
        >
          {t.footer.license}
        </LicenseLink>
        <span className="hidden sm:inline">·</span>
        <span data-testid="footer-credentials" className="inline-flex items-center gap-1.5 text-slate-300">
          <ShieldCheck className="w-3.5 h-3.5 text-brand-accent" strokeWidth={2.5} />
          {t.footer.credentials}
        </span>
      </div>
      <div>© {new Date().getFullYear()} Tenorio Master Roofing. {t.footer.rights}</div>
    </div>

    <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-2 sm:gap-4 text-xs text-slate-500">
      <Link to="/privacy" data-testid="footer-privacy-link" className="text-slate-300 font-semibold hover:text-brand-accent transition-colors">
        {t.footer.privacy}
      </Link>
      <span className="hidden sm:inline">·</span>
      <Link to="/terms" data-testid="footer-terms-link" className="text-slate-300 font-semibold hover:text-brand-accent transition-colors">
        {t.footer.terms}
      </Link>
      <span className="hidden sm:inline">·</span>
      <a
        href={YELP_URL}
        target="_blank"
        rel="noopener noreferrer nofollow"
        data-testid="footer-yelp-link"
        className="inline-flex items-center gap-1.5 text-slate-300 hover:text-brand-accent transition-colors font-semibold"
      >
        <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.5} />
        {t.footer.yelp}
      </a>
    </div>
    <p data-testid="footer-yelp-disclaimer" className="mt-2 text-xs text-slate-500 italic">{t.footer.yelpDisclaimer}</p>
  </>
);
